package one.healthium.uscreen.ui.animation

import androidx.compose.animation.core.Animatable

import androidx.compose.animation.core.tween
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.graphicsLayer
import kotlinx.coroutines.delay
import one.healthium.uscreen.domain.models.AnimationType
import one.healthium.uscreen.domain.models.BlockTransition
import one.healthium.uscreen.domain.models.BmiCardBlock
import one.healthium.uscreen.domain.models.ButtonBlock
import one.healthium.uscreen.domain.models.ChoiceListBlock
import one.healthium.uscreen.domain.models.DividerBlock
import one.healthium.uscreen.domain.models.FeatureListBlock
import one.healthium.uscreen.domain.models.HorizontalRulerBlock
import one.healthium.uscreen.domain.models.ImageBlock
import one.healthium.uscreen.domain.models.LabelBlock
import one.healthium.uscreen.domain.models.NotificationPreviewBlock
import one.healthium.uscreen.domain.models.NumberPickerBlock
import one.healthium.uscreen.domain.models.ProgressSliderBlock
import one.healthium.uscreen.domain.models.ReviewCarouselBlock
import one.healthium.uscreen.domain.models.RowBlock
import one.healthium.uscreen.domain.models.SpacerBlock
import one.healthium.uscreen.domain.models.StackBlock
import one.healthium.uscreen.domain.models.TextInputBlock
import one.healthium.uscreen.domain.models.UScreenBlock
import one.healthium.uscreen.domain.models.VerticalRulerBlock
import one.healthium.uscreen.domain.models.WeightGraphBlock

/** true = navigating back (blocks slide from left), false = forward (blocks slide from right) */
val LocalNavigatingBack = compositionLocalOf { false }

/** Active screen id — blocks compare against this to determine if they're entering or exiting. */
val LocalActiveScreenId = compositionLocalOf { "" }

/** Own screen id — stable for the lifetime of the shell, used by stateful blocks to detect if they are active. */
val LocalScreenOwnId = compositionLocalOf { "" }

private fun UScreenBlock.getTransition(): BlockTransition? = when (this) {
    is ImageBlock -> transition
    is LabelBlock -> transition
    is RowBlock -> transition
    is ChoiceListBlock -> transition
    is TextInputBlock -> transition
    is NumberPickerBlock -> transition
    is VerticalRulerBlock -> transition
    is HorizontalRulerBlock -> transition
    is FeatureListBlock -> transition
    is ProgressSliderBlock -> transition
    is ReviewCarouselBlock -> transition
    is WeightGraphBlock -> transition
    is BmiCardBlock -> transition
    is StackBlock -> transition
    is ButtonBlock -> transition
    is NotificationPreviewBlock -> transition
    is DividerBlock -> transition
    is SpacerBlock -> null
}

/**
 * Animates a block's enter or exit. Phase is determined by [LocalActiveScreenId]:
 * if [screenId] matches active -> entering, otherwise -> exiting.
 */
@Composable
fun Modifier.blockTransition(
    block: UScreenBlock,
    screenId: String = "",
): Modifier {
    if (block is SpacerBlock) return this
    val transition = block.getTransition()
    val activeScreenId = LocalActiveScreenId.current
    val isBack = LocalNavigatingBack.current
    val isExiting = screenId.isNotEmpty() && activeScreenId.isNotEmpty() && screenId != activeScreenId
    val animType = when {
        isExiting && !isBack -> transition?.outAnim ?: DEFAULT_BLOCK_OUT_ANIM  // push exit
        isExiting && isBack -> transition?.inAnim ?: DEFAULT_BLOCK_IN_ANIM     // pop exit (reverse enter)
        !isExiting && !isBack -> transition?.inAnim ?: DEFAULT_BLOCK_IN_ANIM   // push enter
        else -> transition?.outAnim ?: DEFAULT_BLOCK_OUT_ANIM                  // pop enter (reverse exit)
    }
    if (animType == AnimationType.None) return this

    val progress = remember(activeScreenId) { Animatable(if (isExiting) 1f else 0f) }
    LaunchedEffect(activeScreenId) {
        if (isExiting) {
            progress.animateTo(
                targetValue = 0f,
                animationSpec = tween(BLOCK_APPEAR_DURATION.toInt(), easing = TRANSITION_EASING),
            )
        } else {
            val blockDelay = (transition?.delay ?: 0f)
            val totalDelay = (blockDelay * 1000).toLong()
            delay(totalDelay)
            progress.animateTo(
                targetValue = 1f,
                animationSpec = tween(BLOCK_APPEAR_DURATION.toInt(), easing = TRANSITION_EASING),
            )
        }
    }

    return this.graphicsLayer {
        val p = progress.value
        val remaining = 1f - p
        when (animType) {
            AnimationType.SlideOut -> {
                alpha = p
                translationX = when {
                    isExiting && isBack -> remaining * size.width
                    isExiting -> -remaining * size.width
                    isBack -> -remaining * size.width
                    else -> remaining * size.width
                }
            }

            AnimationType.Fade -> {
                alpha = p
            }

            AnimationType.None -> {}
        }
    }
}
